<template>
  <div
    v-if="phase === 'chat'"
    :class="
      variant === 'docked'
        ? 'flex items-center justify-end gap-1 border-b border-gray-100 px-2 py-1'
        : 'flex items-center gap-1'
    "
  >
    <div class="relative" data-history-dropdown>
      <button
        ref="historyTriggerRef"
        v-tooltip="'Chat history'"
        class="rounded-md p-1 text-gray-400 hover:text-gray-600"
        aria-haspopup="true"
        :aria-expanded="showHistory"
        @click="showHistory = !showHistory"
      >
        <span class="sr-only">Chat history</span>
        <Icon name="list-ul" class="size-4" />
      </button>
      <!-- Teleported to body to escape the panel's overflow-hidden; positioned
         under the trigger and right-aligned to it. -->
      <Teleport to="body">
        <div
          v-if="showHistory"
          role="menu"
          data-history-dropdown
          class="fixed z-50 w-72 rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          :style="historyMenuStyle"
        >
          <p class="px-3 py-1.5 text-xs font-semibold text-gray-500">
            Recent chats
          </p>
          <div class="max-h-64 overflow-y-auto">
            <ChatHistoryItem
              v-for="s in recentSessions"
              :key="s.id"
              :session-id="s.id"
              :title="s.title"
              row-class="gap-2 px-3 py-2"
              :button-class="
                s.id === sessionId ? 'bg-purple-50' : 'hover:bg-gray-50'
              "
              @select="switchSession"
            >
              <Icon
                name="comment-dots"
                class="size-3.5 shrink-0 text-gray-400"
              />
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm text-gray-700">
                  {{ s.title }}
                </p>
                <p class="text-xs text-gray-400">
                  {{ formatRelativeTime(s.modifiedAt) }}
                </p>
              </div>
            </ChatHistoryItem>
            <p
              v-if="recentSessions.length === 0"
              class="px-3 py-2 text-xs text-gray-400"
            >
              No previous chats
            </p>
          </div>
        </div>
      </Teleport>
    </div>
    <button
      v-tooltip="'New session'"
      class="rounded-md p-1 text-gray-400 hover:text-gray-600"
      @click="restart"
    >
      <span class="sr-only">New session</span>
      <Icon name="redo" class="size-4" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { DateTime } from "luxon";
import { useElementBounding } from "@vueuse/core";
import Icon from "@/components/Core/Icon.vue";
import ChatHistoryItem from "@/components/AIAgent/ChatHistoryItem.vue";
import { useAISessionsQuery } from "@/services/ai_agent";
import { useAiChatSession } from "@/composables/useAiChatSession";

defineProps({
  // Docked variant renders as its own bordered secondary row under the tab
  // strip; overlay variant sits inline in the panel header.
  variant: { type: String, default: "overlay" },
});

const { phase, sessionId, resumeSession, restart } = useAiChatSession();

const sessions = useAISessionsQuery();
const showHistory = ref(false);
const historyTriggerRef = ref<HTMLElement | null>(null);
const { bottom: historyTriggerBottom, right: historyTriggerRight } =
  useElementBounding(historyTriggerRef);
const historyMenuStyle = computed(() => ({
  top: `${historyTriggerBottom.value + 4}px`,
  right: `${window.innerWidth - historyTriggerRight.value}px`,
}));

const MAX_RECENT = 10;
const recentSessions = computed(
  () => sessions.data?.slice(0, MAX_RECENT) ?? [],
);

function formatRelativeTime(timestamp: string): string {
  return DateTime.fromISO(timestamp).toRelative() ?? "";
}

function switchSession(id: number): void {
  showHistory.value = false;
  resumeSession(id);
}

function onClickOutsideHistory(e: MouseEvent): void {
  if (showHistory.value) {
    // composedPath, not target.closest: a row's delete/confirm click re-renders
    // the row before this listener runs, so the clicked button is already
    // detached and closest() would read it as an outside click.
    const insideDropdown = e
      .composedPath()
      .some(
        (el) =>
          el instanceof HTMLElement && el.hasAttribute("data-history-dropdown"),
      );
    if (!insideDropdown) {
      showHistory.value = false;
    }
  }
}

onMounted(() => document.addEventListener("click", onClickOutsideHistory));
onBeforeUnmount(() =>
  document.removeEventListener("click", onClickOutsideHistory),
);
</script>
